import React, { useEffect, memo } from 'react';
import { useForm, useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { FORM_VALUES } from '../../../../../utils/helpers/constants';
import { RentalType } from '../../components/RentalType';
import { FormBlockWrapper } from '../FormBlockWrapper';
import { FormTextInput } from '../../components/FormTextInput';
import { Checkbox } from '../../../../ui/Checkbox';
import {
  CheckBoxesContainer,
  CheckBoxWrapper,
  ManatIcon,
  PricePlaceholder
} from './styled';
import { CurrencySymbol } from '../../../../ui/ItemCard/CurrencySymbol';
import { PricePerAcre } from '../../components/PricePerAcre';
import { checkIsAreaCategory } from '../../../../../utils/helpers/common';
import { getPriceTranslationKey } from '../../../../../utils/helpers/getPriceTranslationKey';

const units = (
  <ManatIcon>
    <CurrencySymbol currency="AZN" />
  </ManatIcon>
);

const PricingComponent: React.FC = () => {
  const { t } = useTranslation();
  const { change } = useForm();
  const { input: categoriesIdField } = useField(FORM_VALUES.categoryId, {
    subscription: { value: true }
  });
  const { input: leasedField } = useField(FORM_VALUES.leased, {
    subscription: { value: true }
  });
  const { input: areaField } = useField(FORM_VALUES.area, {
    subscription: { value: true }
  });
  const { input: priceField } = useField(FORM_VALUES.price, {
    subscription: { value: true }
  });
  const { input: itemIdField } = useField('itemId', {
    subscription: { value: true }
  });
  const { input: hasBillOfSaleField } = useField(FORM_VALUES.hasBillOfSale, {
    subscription: { value: true }
  });
  const { input: hasMortgageField } = useField(FORM_VALUES.hasMortgage, {
    subscription: { value: true }
  });
  const leased = leasedField.value?.toString() === 'true';
  const categoryId = categoriesIdField.value;

  useEffect(() => {
    if (!itemIdField.value) {
      if (leased) {
        change(FORM_VALUES.hasBillOfSale, undefined);
        change(FORM_VALUES.hasMortgage, undefined);
      } else {
        change(FORM_VALUES.hasBillOfSale, false);
        change(FORM_VALUES.hasMortgage, false);
      }
    }
  }, [leased, itemIdField.value, change]);

  const onCheckboxClick = (field: string, newValue: boolean) => {
    change(field, newValue);
  };

  const pricePlaceholder = (
    <PricePlaceholder>
      {`${t(getPriceTranslationKey(categoryId, leased))},`}
      {units} *
    </PricePlaceholder>
  );

  const hasBillOfSale = hasBillOfSaleField.value;
  const hasMortgage = hasMortgageField.value;
  const isArea = checkIsAreaCategory(categoryId);
  return (
    <FormBlockWrapper header={t('new_ad.price_conditions')} required>
      {leased && <RentalType />}

      <FormTextInput
        inputMode="numeric"
        name={FORM_VALUES.price}
        placeholder={pricePlaceholder}
        isEnabledAutoSelectionRange={false}
        hasErrorField
      />

      {isArea && !leased && (
        <PricePerAcre area={areaField.value} price={priceField.value} />
      )}

      {!leased && (
        <CheckBoxesContainer>
          <CheckBoxWrapper data-cy="bill-of-sale">
            <Checkbox
              id="sale"
              cypress="bill-of-sale-button"
              onChangeHandler={() =>
                onCheckboxClick(FORM_VALUES.hasBillOfSale, !hasBillOfSale)
              }
              name={FORM_VALUES.hasBillOfSale}
              isChecked={hasBillOfSale}
              label={t('search.form.has_bill_of_sale')}
            />
          </CheckBoxWrapper>
          <CheckBoxWrapper data-cy="mortgage">
            <Checkbox
              id="mortgage"
              cypress="mortgage-button"
              onChangeHandler={() =>
                onCheckboxClick(FORM_VALUES.hasMortgage, !hasMortgage)
              }
              name={FORM_VALUES.hasMortgage}
              isChecked={hasMortgage}
              label={t('search.form.has_mortgage')}
            />
          </CheckBoxWrapper>
        </CheckBoxesContainer>
      )}
    </FormBlockWrapper>
  );
};

export const Pricing = memo(PricingComponent);
